/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { useQueries, useQuery, UseQueryResult } from '@tanstack/react-query';
import {
  MultiStoreCenterDates,
  MultiStoreCenterResult,
  MultiStoreCenterTimes,
  MultiStoreOkResponse,
  MultiStorePointOfSale,
  MultiStorePointOfSaleResponse,
  MultiStorePointOfSalesRequest,
  MultiStoreSlotDetailsRequest,
  MultiStoreSlotDetailsResponse,
  MultiStoreSlotsRequest,
  MultiStoreSlotsResponse,
} from '../interface/multi-store';
import { silentClient } from '../lib/client/silent-client';
import { TOKEN } from '../lib/info';
import { toApiDate } from '../utils/multi-store/dates';
import {
  toCenterDates,
  toCenterTimes,
  toMultiStorePointOfSale,
} from '../utils/multi-store/responses';

// Smart multi-store (solo aziende FOCUS CLOUD, il backend lo ricontrolla a ogni chiamata).
// Client silenzioso: gli errori fanno reject e React Query li espone come isError.
// Il signal di React Query annulla le richieste che nessuno osserva più (es. settimana
// cambiata più volte di fila); niente retry: un centro lento o in errore non deve far
// aspettare il cliente, risulta solo non disponibile.

export const multiStorePointOfSales = async (
  request: MultiStorePointOfSalesRequest,
  signal?: AbortSignal
): Promise<MultiStorePointOfSale[]> => {
  const { data } = await silentClient.post<
    MultiStoreOkResponse<MultiStorePointOfSaleResponse[]>
  >(`/api/MultiStore/PointOfSales/${TOKEN() ?? ''}`, request, { signal });
  return (data?.data ?? []).map(toMultiStorePointOfSale);
};

export const multiStoreSlots = async (
  request: MultiStoreSlotsRequest,
  signal?: AbortSignal
): Promise<MultiStoreCenterDates | null> => {
  const { data } = await silentClient.post<
    MultiStoreOkResponse<MultiStoreSlotsResponse>
  >(`/api/MultiStore/Slots/${TOKEN() ?? ''}`, request, { signal });
  return toCenterDates(data?.data, request.PointOfSaleIds[0]);
};

export const multiStoreSlotDetails = async (
  request: MultiStoreSlotDetailsRequest,
  signal?: AbortSignal
): Promise<MultiStoreCenterTimes | null> => {
  const { data } = await silentClient.post<
    MultiStoreOkResponse<MultiStoreSlotDetailsResponse>
  >(`/api/MultiStore/SlotDetails/${TOKEN() ?? ''}`, request, { signal });
  return toCenterTimes(data?.data, request.PointOfSaleIds[0]);
};

const toCenterResult = <T>(
  result: UseQueryResult<T | null>
): MultiStoreCenterResult<T> => ({
  isPending: result.isPending,
  isFetching: result.isFetching,
  isUnavailable: result.isError || result.data === null,
  data: result.data ?? undefined,
});

// Centri alternativi della stessa insegna: parte al montaggio di "quando" con il gate vero,
// serve a sapere se mostrare il riquadro. Con la funzione non abilitata il backend risponde [].
export const useMultiStorePointOfSales = (
  ServiceId: string,
  OriginPointOfSaleId: string,
  Latitude?: number,
  Longitude?: number,
  FilterPointOfSaleIds?: string[]
) => {
  return useQuery({
    queryFn: async ({ signal }) =>
      await multiStorePointOfSales(
        {
          ServiceId,
          OriginPointOfSaleId,
          Latitude,
          Longitude,
          FilterPointOfSaleIds,
        },
        signal
      ),
    queryKey: [
      'multi-store-point-of-sales',
      TOKEN(),
      ServiceId,
      OriginPointOfSaleId,
      Latitude,
      Longitude,
      FilterPointOfSaleIds,
    ],
    retry: 0,
    throwOnError: false,
    // l'elenco dei centri non cambia durante la prenotazione: niente richiesta a ogni montaggio
    staleTime: 5 * 60 * 1000,
  });
};

// Una query per centro (PointOfSaleIds: [id]): aggiungere o togliere un centro non
// ricalcola gli altri. Date dello store 'YYYY/MM/DD', convertite per le API.
export const useMultiStoreSlots = (
  pointOfSaleIds: string[],
  ServiceId: string,
  StartDate: string,
  EndDate: string,
  UserId?: string,
  FilterPointOfSaleIds?: string[]
): MultiStoreCenterResult<MultiStoreCenterDates>[] => {
  return useQueries({
    queries: pointOfSaleIds.map((pointOfSaleId) => ({
      queryFn: async ({ signal }: { signal: AbortSignal }) =>
        await multiStoreSlots(
          {
            ServiceId,
            PointOfSaleIds: [pointOfSaleId],
            UserId,
            StartDate: toApiDate(StartDate),
            EndDate: toApiDate(EndDate),
            FilterPointOfSaleIds,
          },
          signal
        ),
      queryKey: [
        'multi-store-slots',
        TOKEN(),
        ServiceId,
        pointOfSaleId,
        StartDate,
        EndDate,
        UserId,
        FilterPointOfSaleIds,
      ],
      retry: 0,
      throwOnError: false,
    })),
    combine: (results) =>
      results.map((result) => toCenterResult<MultiStoreCenterDates>(result)),
  });
};

export const useMultiStoreSlotDetails = (
  pointOfSaleIds: string[],
  ServiceId: string,
  date?: string,
  UserId?: string,
  FilterPointOfSaleIds?: string[]
): MultiStoreCenterResult<MultiStoreCenterTimes>[] => {
  return useQueries({
    queries: pointOfSaleIds.map((pointOfSaleId) => ({
      queryFn: async ({ signal }: { signal: AbortSignal }) =>
        await multiStoreSlotDetails(
          {
            ServiceId,
            PointOfSaleIds: [pointOfSaleId],
            UserId,
            Date: toApiDate(date ?? ''),
            FilterPointOfSaleIds,
          },
          signal
        ),
      queryKey: [
        'multi-store-slot-details',
        TOKEN(),
        ServiceId,
        pointOfSaleId,
        date,
        UserId,
        FilterPointOfSaleIds,
      ],
      enabled: date !== undefined,
      retry: 0,
      throwOnError: false,
    })),
    combine: (results) =>
      results.map((result) => toCenterResult<MultiStoreCenterTimes>(result)),
  });
};
